(function () {
  if (customElements.get('ce-filters-price-range') !== undefined) {
    return;
  }

  class FiltersPriceRange extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.selectors = {
        collection: 'ce-collection',
        inputRangeMin: '[data-filters-price-range-input-range-min]',
        inputRangeMax: '[data-filters-price-range-input-range-max]',
        inputTextMin: '[data-filters-price-range-input-text-min]',
        inputTextMax: '[data-filters-price-range-input-text-max]',
      };

      this.elements = null;

      this.range = this.getAttribute('range');
      this.willAdjustInputsText = false;

      this.handleInputInputRangeMin = this.handleInputInputRangeMin.bind(this);
      this.handleInputInputRangeMax = this.handleInputInputRangeMax.bind(this);
      this.handleInputInputTextMin = this.handleInputInputTextMin.bind(this);
      this.handleInputInputTextMax = this.handleInputInputTextMax.bind(this);
      this.handleChangeInputRangeMin = this.handleChangeInputRangeMin.bind(this);
      this.handleChangeInputRangeMax = this.handleChangeInputRangeMax.bind(this);
      this.handleChangeInputTextMin = this.handleChangeInputTextMin.bind(this);
      this.handleChangeInputTextMax = this.handleChangeInputTextMax.bind(this);
    }

    get min() {
      return parseInt(this.getAttribute('min'));
    }

    set min(value) {
      this.setAttribute('min', value);
    }

    get max() {
      return parseInt(this.getAttribute('max'));
    }

    set max(value) {
      this.setAttribute('max', value);
    }

    static get observedAttributes() {
      return ['min', 'max'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'min':
          if (newValue !== null) {
            requestAnimationFrame(() => {
              this.style.setProperty('--min', newValue);
            });

            this.elements.inputRangeMin.setAttribute('aria-valuenow', newValue);
            this.elements.inputRangeMin.setAttribute('aria-valuetext', formatMoney(parseInt(newValue) * 100));

            if (this.willAdjustInputsText) {
              this.elements.inputTextMin.value = newValue;
            }
          }

          break;
        case 'max':
          if (newValue !== null) {
            requestAnimationFrame(() => {
              this.style.setProperty('--max', newValue);
            });

            this.elements.inputRangeMax.setAttribute('aria-valuenow', newValue);
            this.elements.inputRangeMax.setAttribute('aria-valuetext', formatMoney(parseInt(newValue) * 100));

            if (this.willAdjustInputsText) {
              this.elements.inputTextMax.value = newValue;
            }
          }

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        collection: document.querySelector(this.selectors.collection),
        inputRangeMin: this.querySelector(this.selectors.inputRangeMin),
        inputRangeMax: this.querySelector(this.selectors.inputRangeMax),
        inputTextMin: this.querySelector(this.selectors.inputTextMin),
        inputTextMax: this.querySelector(this.selectors.inputTextMax),
      };

      this.elements.inputRangeMin.addEventListener('input', this.handleInputInputRangeMin);
      this.elements.inputRangeMax.addEventListener('input', this.handleInputInputRangeMax);
      this.elements.inputTextMin.addEventListener('input', this.handleInputInputTextMin);
      this.elements.inputTextMax.addEventListener('input', this.handleInputInputTextMax);
      this.elements.inputRangeMin.addEventListener('change', this.handleChangeInputRangeMin);
      this.elements.inputRangeMax.addEventListener('change', this.handleChangeInputRangeMax);
      this.elements.inputTextMin.addEventListener('change', this.handleChangeInputTextMin);
      this.elements.inputTextMax.addEventListener('change', this.handleChangeInputTextMax);

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      this.elements.inputRangeMin.removeEventListener('input', this.handleInputInputRangeMin);
      this.elements.inputRangeMax.removeEventListener('input', this.handleInputInputRangeMax);
      this.elements.inputTextMin.removeEventListener('input', this.handleInputInputTextMin);
      this.elements.inputTextMax.removeEventListener('input', this.handleInputInputTextMax);
      this.elements.inputRangeMin.removeEventListener('change', this.handleChangeInputRangeMin);
      this.elements.inputRangeMax.removeEventListener('change', this.handleChangeInputRangeMax);
      this.elements.inputTextMin.removeEventListener('change', this.handleChangeInputTextMin);
      this.elements.inputTextMax.removeEventListener('change', this.handleChangeInputTextMax);
    }

    handleInputInputRangeMin(event) {
      this.elements.collection.abortFetch();

      if (!this.willAdjustInputsText) {
        this.elements.inputTextMax.value = this.max;
        this.willAdjustInputsText = true;
      }

      const value = parseInt(event.target.value);

      if (value >= this.max) {
        this.elements.inputRangeMin.value = this.max - 1;
        return;
      }

      this.min = value;
    }

    handleInputInputRangeMax(event) {
      this.elements.collection.abortFetch();

      if (!this.willAdjustInputsText) {
        this.elements.inputTextMin.value = this.min;
        this.willAdjustInputsText = true;
      }

      const value = parseInt(event.target.value);

      if (value <= this.min) {
        this.elements.inputRangeMax.value = this.min + 1;
        return;
      }

      this.max = value;
    }

    handleInputInputTextMin() {
      this.elements.collection.abortFetch();
    }

    handleInputInputTextMax() {
      this.elements.collection.abortFetch();
    }

    handleChangeInputRangeMin(event) {
      this.elements.collection.applyFilters(event.target);
    }

    handleChangeInputRangeMax(event) {
      this.elements.collection.applyFilters(event.target);
    }

    handleChangeInputTextMin(event) {
      const value = parseInt(event.target.value, 10);
      let newValue = 0;

      if (isNaN(value)) {
        newValue = this.min;
      } else if (value >= this.max) {
        newValue = this.max - 1;
      } else {
        newValue = value;
      }

      this.elements.inputTextMin.value = newValue;
      this.elements.inputRangeMin.value = newValue;
      this.elements.inputRangeMin.dispatchEvent(new Event('input'));

      this.elements.collection.applyFilters(event.target);
    }

    handleChangeInputTextMax(event) {
      const value = parseInt(event.target.value, 10);
      let newValue = 0;

      if (isNaN(value)) {
        newValue = this.max;
      } else if (value <= this.min) {
        newValue = this.min + 1;
      } else if (value > this.range) {
        newValue = this.range;
      } else {
        newValue = value;
      }

      this.elements.inputTextMax.value = newValue;
      this.elements.inputRangeMax.value = newValue;
      this.elements.inputRangeMax.dispatchEvent(new Event('input'));

      this.elements.collection.applyFilters(event.target);
    }
  }

  customElements.define('ce-filters-price-range', FiltersPriceRange);
})();
